import {createContext, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useQueryClient} from "@tanstack/react-query";
import api, { authBus } from "../lib/api";
import {User} from "../types/user";
import {logEvent} from "../analytics/events";

interface AuthCtx {
    user: User | null;
    isReady: boolean;
    isAuthenticated: boolean;

    login: (email: string, password: string) => Promise<User>;
    signup: (firstName: string, lastName: string, email: string, password: string) => Promise<User>;

    oauthComplete: (idToken: string, refreshToken: string) => Promise<User>;

    forgotPassword: (email: string) => Promise<string>;
    resetPassword: (email: string, code: string, newPassword: string) => Promise<User>;

    logout: () => void;
    refreshUser: () => Promise<User>;
}

interface LoginResponse {
    idToken: string;
    refreshToken: string;
}

/** ─────────────────────────────── Constants ─────────────────────────────── */
const ID_TOKEN_KEY = "id_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const USER_KEY = "auth_user";

/** ─────────────────────────────── Utilities ─────────────────────────────── */
function decodeJwt<T = any>(token: string): T | null {
    try {
        const [, payload] = token.split(".");
        const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
        const padded = base64 + "===".slice((base64.length + 3) % 4);
        const json = atob(padded);
        return JSON.parse(json);
    } catch {
        return null;
    }
}

function readStoredTokens(): LoginResponse | null {
    const idToken = localStorage.getItem(ID_TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!idToken || !refreshToken) return null;
    return {idToken, refreshToken};
}

function writeTokens(tokens: LoginResponse) {
    localStorage.setItem(ID_TOKEN_KEY, tokens.idToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
}

function readStoredUser(): User | null {
    try {
        const raw = localStorage.getItem(USER_KEY);
        if (!raw) return null;
        const u = JSON.parse(raw);
        if (!u?.id || !u?.name) return null;
        return {
            id: String(u.id),
            name: String(u.name),
            email: String(u.email),
            avatarUrl: u.avatarUrl ?? undefined,
            roles: Array.isArray(u.roles) ? u.roles : [],
            permissions: Array.isArray(u.permissions) ? u.permissions : [],
        } as User;
    } catch {
        return null;
    }
}

function writeStoredUser(u: User) {
    localStorage.setItem(USER_KEY, JSON.stringify(u));
}

function clearStoredUser() {
    localStorage.removeItem(USER_KEY);
}

export function clearStorage() {
    localStorage.clear();
}

// Everything tied to the signed-in account. Basket, consent and saved events are not.
const SESSION_KEYS = [ID_TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY, "active_business_id"];

function clearSessionStorage() {
    SESSION_KEYS.forEach((k) => localStorage.removeItem(k));
}

/** ─────────────────────────────── API helpers ───────────────────────────── */
async function apiLogin(email: string, password: string): Promise<LoginResponse> {
    const {data} = await api.post("/api/v2/auth/login", {email, password});
    const idToken: string | null = data?.idToken ?? null;
    const refreshToken: string | null = data?.refreshToken ?? null;
    if (!idToken || !refreshToken) throw new Error("Login response missing tokens");
    return {idToken, refreshToken};
}

async function apiSignup(firstName: string, lastName: string, email: string, password: string): Promise<void> {
    await api.post("/api/v1/auth/signUp", {firstName, lastName, email, password});
}

async function apiForgotPassword(email: string): Promise<any> {
    return await api.post("/api/v2/auth/forgot-password", {email});
}

async function apiResetPassword(email: string, code: string, newPassword: string): Promise<any> {
    return await api.post("/api/v2/auth/reset-password", {email, code, newPassword});
}

async function fetchCurrentUser(): Promise<User> {
    const {data} = await api.get("/api/v1/users");
    return {
        id: String(data.id),
        name: String(data.name ?? "User"),
        email: String(data.email),
        avatarUrl: data.avatarUrl ?? undefined,
        roles: Array.isArray(data.roles) ? data.roles : [],
        permissions: Array.isArray(data.permissions) ? data.permissions : [],
    };
}

/** ───────────────────────────── Context & Hook ──────────────────────────── */
const AuthContext = createContext<AuthCtx>({} as AuthCtx);
export const useAuth = () => useContext(AuthContext);

/** ────────────────────────────── Provider ──────────────────────────────── */
export function AuthProvider({children}: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isReady, setIsReady] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const hydrating = useRef(false);
    // Cached queries belong to the session that fetched them; drop them on logout.
    const queryClient = useQueryClient();

    // Hydrate on first mount: set tokens for api, then load user & business
    useEffect(() => {
        (async () => {
            hydrating.current = true;
            try {
                const tokens = readStoredTokens();
                const cachedUser = readStoredUser();

                if (tokens) {
                    const claims = decodeJwt<{ exp?: number }>(tokens.idToken);
                    const exp = claims?.exp ? claims.exp * 1000 : 0;
                    const needsRefresh = Date.now() >= exp - 2 * 60 * 1000; // 2 min early

                    // Seed the api client with the stored tokens so the shared refresh
                    // and the request interceptor have something to work with.
                    api.setTokens(tokens.idToken, tokens.refreshToken);

                    if (needsRefresh) {
                        // Use the single shared refresh path (api.ts) so we never race a
                        // second, independent refresh against a rotating refresh token.
                        const newId = await api.refreshIdToken();
                        if (!newId) {
                            // refreshIdToken already cleared tokens + dispatched auth:failed.
                            clearStorage();
                            setUser(null);
                            setIsAuthenticated(false);
                            hydrating.current = false;
                            setIsReady(true);
                            return;
                        }
                    }

                    setIsAuthenticated(true);

                    // Optimistic UI from cache
                    if (cachedUser) setUser(cachedUser);

                    // Always try to refresh both
                    const [uRes] = await Promise.allSettled([
                        fetchCurrentUser(),
                    ]);

                    if (uRes.status === "fulfilled") {
                        writeStoredUser(uRes.value);
                        setUser(uRes.value);
                    }
                } else {
                    api.clearTokens();
                    clearStoredUser();
                    setUser(null);
                    setIsAuthenticated(false);
                }
            } finally {
                hydrating.current = false;
                setIsReady(true);
            }
        })();

        // Cross-tab sync
        const onStorage = () => {
            if (hydrating.current) return;
            const tokens = readStoredTokens();
            const cachedUser = readStoredUser();

            if (tokens) {
                api.setTokens(tokens.idToken, tokens.refreshToken);
                setUser(cachedUser);
                setIsAuthenticated(true);
            } else {
                api.clearTokens();
                setUser(null);
                setIsAuthenticated(false);
            }
        };

        // When a token refresh fails mid-session, clear auth state so the user
        // is prompted to log in rather than stuck in a zombie authenticated state.
        const onAuthFailed = () => {
            api.clearTokens();
            clearStoredUser();
            queryClient.clear();
            setUser(null);
            setIsAuthenticated(false);
        };

        window.addEventListener("storage", onStorage);
        authBus.addEventListener("auth:failed", onAuthFailed);
        return () => {
            window.removeEventListener("storage", onStorage);
            authBus.removeEventListener("auth:failed", onAuthFailed);
        };
    }, []);

    // A login starts from a clean slate, as a private window would: no tokens, cached
    // user, selected business or cached queries from an earlier account carry over.
    const beginSession = (tokens: LoginResponse) => {
        clearSessionStorage();
        queryClient.clear();
        writeTokens(tokens);
        api.setTokens(tokens.idToken, tokens.refreshToken);
        setUser(null);
        setIsAuthenticated(true);
    };

    const login = async (email: string, password: string): Promise<User> => {
        const tokens = await apiLogin(email, password);
        beginSession(tokens);

        const [u] = await Promise.all([fetchCurrentUser()]);
        writeStoredUser(u);
        setUser(u);
        logEvent("login", {method: "password"});
        return u;
    };

    const signup = async (firstName: string, lastName: string, email: string, password: string): Promise<User> => {
        await apiSignup(firstName, lastName, email, password);

        const tokens = await apiLogin(email, password);
        beginSession(tokens);

        const [u] = await Promise.all([fetchCurrentUser()]);
        writeStoredUser(u);
        setUser(u);
        logEvent("sign_up", {method: "password"});
        return u;
    }

    const forgotPassword = async (email: string): Promise<string> => {
        return await apiForgotPassword(email);
    }

    const resetPassword = async (email: string, code: string, newPassword: string): Promise<User> => {
        await apiResetPassword(email, code, newPassword);

        const tokens = await apiLogin(email, newPassword);
        beginSession(tokens);

        const [u] = await Promise.all([fetchCurrentUser()]);
        writeStoredUser(u);
        setUser(u);
        return u;
    }


    const refreshUser = async (): Promise<User> => {
        const fresh = await fetchCurrentUser();
        writeStoredUser(fresh);
        setUser(fresh);
        return fresh;
    };

    const logout = () => {
        clearStorage();
        api.clearTokens();
        queryClient.clear();
        setUser(null);
        setIsAuthenticated(false);
    };

    const oauthComplete = async (idToken: string, refreshToken: string) => {
        beginSession({idToken, refreshToken});

        const claims = decodeJwt<{ email?: string; name?: string; sub?: string }>(idToken);
        const name = claims?.name || claims?.email || "User";
        const sub = claims?.sub || "";
        const email = claims?.email || "";

        // ensure user exists server-side
        await api.post("/api/v1/auth/ensure-user", {name, sub, email});

        // fetch user & cache
        const fresh = await fetchCurrentUser();
        writeStoredUser(fresh);
        setUser(fresh);
        logEvent("login", {method: "google"});
        return fresh;
    };

    const value = useMemo<AuthCtx>(
        () => ({
            user,
            isReady,
            isAuthenticated,
            login,
            signup,
            forgotPassword,
            resetPassword,
            oauthComplete,
            logout,
            refreshUser
        }),
        [user, isReady, isAuthenticated]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
